import { WNATIVE } from '@emberswap/emberswap-sdk2/dist/constants'
import {  Native, RToken, Token, Trade } from '@emberswap/emberswap-sdk2/dist/entities'
import { TradeType, Version } from '@emberswap/emberswap-sdk2/dist/enums'
import { Type } from '@emberswap/emberswap-sdk2/dist/types'
import Image from 'next/image'
import React, { Dispatch, FC, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useTokens } from '../hooks/Token/useTokens'
import { useCustomTokens } from '../state/user/hooks'
import { Chip } from './Chip'
import { Currency } from './Currency'
import { Dialog } from './Dialog'
import { SwapStatsProps } from './SwapStatsDisclosure'
import { Tooltip } from './Tooltip'

export type TradeOutput =
  | Trade<Type, Type, TradeType.EXACT_INPUT | TradeType.EXACT_OUTPUT, Version.V1 | Version.V2>
  | undefined

export interface RouteProps extends SwapStatsProps {
  trade: TradeOutput
  open: boolean
  setOpen: Dispatch<SetStateAction<boolean>>
}
const tokenFromRToken = (
  token: RToken,

) => {
    if (token.address === '' || !token.address) return Native.onChain(Number(token.chainId))
     return new Token({
       address: token.address,
       symbol: token.symbol,
       chainId: Number(token.chainId),
       decimals: 18,
     })
}

// Can render a tines multi route
export const Route: FC<RouteProps> = ({ open, setOpen, trade, chainId }) => {
  const onClose = useCallback(() => {
    setOpen(false)
  }, [setOpen])
  const tokenMap = useTokens(chainId)
  const [customTokensMap] = useCustomTokens(chainId)
  const tokens = useMemo(
    () => Object.values({ ...tokenMap, ...customTokensMap }),
    [tokenMap, customTokensMap]
  )

  return (
    <Dialog open={open} onClose={onClose}>
      <Dialog.Content className="max-h-[460px] !pb-[20px] overflow-y-auto scroll rounded-xl bg-black/[0.24] p-2 border border-slate-200/10">
        <Dialog.Header border={false} title="Optimized Route" onClose={onClose} />
          <div className="flex flex-col gap-4">
            {trade?.route?.legs?.map((directPath, i) => {
              if (directPath.poolType == 'Wrap') {
                return <></>
              }
                const t0 = tokens.filter(
                  (t) => t.address.toLowerCase() === directPath.tokenFrom.address.toLowerCase()
                )
              const t1 = tokens.filter(
                (t) => t.address.toLowerCase() === directPath.tokenTo.address.toLowerCase()
              )
              return (
                <ComplexRoutePath
                  trade={trade}
                  key={i}
                  poolAddress={directPath.poolAddress}
                  fromToken={t0?.[0] ? t0[0] : tokenFromRToken(directPath.tokenFrom)}
                  toToken={t1?.[0] ? t1[0] : tokenFromRToken(directPath.tokenTo)}
                  poolType={directPath.poolType}
                  poolFee={directPath.poolFee}
                  portion={directPath.absolutePortion}
                  title={`${directPath.poolName}`}
                  exchangeUri={directPath.exchangeUri!}
                  chainId={chainId}
                />
              )
            })}
          </div>
      </Dialog.Content>
    </Dialog>
  )
}

interface ComplexRoutePathProps {
  trade: TradeOutput
  fromToken: Type
  toToken: Type
  poolType: 'Stable' | 'Classic' | 'Unknown' | 'Wrap'
  poolFee: number
  portion: number
  title: string
  poolAddress: string
  exchangeUri:string
  chainId: number
}

const ComplexRoutePath: FC<ComplexRoutePathProps> = ({
  trade,
  poolAddress,
  fromToken,
  toToken,
  poolType,
  poolFee,
  portion,
  title,
  exchangeUri,
  chainId,
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)

  useEffect(() => {
    if (ref.current) {
      setWidth((ref.current.offsetWidth - 28) * Number(portion))
    }
  }, [portion])

  const fromTokenSymbol =
    fromToken.name === WNATIVE[chainId].name &&
    fromToken.name?.startsWith('Wrapped') &&
    fromToken.symbol?.toUpperCase().startsWith('W')
      ? fromToken.symbol?.slice(1)
      : fromToken.symbol
  const toTokenSymbol =
    toToken.name === WNATIVE[chainId].name &&
    toToken.name?.startsWith('Wrapped') &&
    toToken.symbol?.toUpperCase().startsWith('W')
      ? toToken.symbol?.slice(1)
      : toToken.symbol
  return (
    <div className="relative grid grid-cols-10">
      <div className="absolute inset-0 z-0 flex items-center pointer-events-none">
        <svg viewBox="850 0 300 200" width="100%" height="35" className="text-slate-700">
          <line
            x1="0"
            x2="3000"
            y1="100"
            y2="100"
            stroke="currentColor"
            strokeWidth="20"
            strokeLinecap="round"
            strokeDasharray="1, 45"
          />
        </svg>
      </div>
      <div className="z-[10] col-span-5 flex justify-start items-center">
        <div
          ref={ref}
          className="relative flex items-center justify-between gap-2 p-2 overflow-hidden border-2 rounded-full border-slate-900 bg-slate-900"
        >
          <div
            className="absolute inset-0 rounded-full pointer-events-none bg-slate-800"
            style={{ width: `calc(28px + ${width}px)` }}
          />
          <div className="z-[10] flex items-center gap-1">
            <Currency.Icon disableLink currency={fromToken} width={16} height={16} />
            <p className="text-xs font-semibold text-slate-200">{fromTokenSymbol}</p>
          </div>
          <p className="z-[10] text-slate-400 font-semibold text-xs gap-1">
            {Number(portion * 100).toFixed(2)}%
          </p>
        </div>
      </div>
      <div className="z-[10] cursor-pointer col-span-1 flex justify-center items-center font-semibold text-xs gap-1 bg-slate-800 text-slate-200  border border-slate-200/10 rounded-full h-[36px] px-2">
        <Tooltip
          className="mt-1 pointer-events-none"
          key={title}
          button={<Image key={title} alt={title} width={20} height={20} src={exchangeUri} />}
          panel={
            <div className="flex flex-col gap-2">
              <p className="flex gap-1.5 items-end text-slate-400 font-semibold text-xs">
                <Chip color="gray" size="sm" label={poolType} />
                <Chip color="gray" size="sm" label={`Fee ${poolFee * 100}%`} />
                <Chip color="gray" size="sm" label={title.split(' ')[0]} />
              </p>
            </div>
          }
        />
      </div>
      <div className="z-[10] col-span-4 flex justify-end items-center">
        <div className="p-0.5 rounded-full bg-slate-900">
          <div className="px-2 bg-slate-700 h-[36px] rounded-full flex items-center gap-1">
            <Currency.Icon disableLink currency={toToken} width={16} height={16} />
            <p className="text-xs font-semibold text-slate-200">{toTokenSymbol}</p>
          </div>
        </div>
      </div>
    </div>
  )
}
